import React, { useState, useEffect } from 'react';
import {
  Box,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  VStack,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
  Select,
  FormControl,
  FormLabel,
  Text,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../lib/firebase';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  setDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { Prediction, Match, User, LeagueId } from '../../types';
import {
  getJornadaLabel,
  getMatchIdsForMundialJornada,
  getMatchIdsForMundialKnockoutPhase,
  getMundialKnockoutMatches,
  getKnockoutPhaseLabel,
  MUNDIAL_GROUP_JORNADAS,
  parseLigaMxJornadaNumber,
  sortKnockoutPhases,
  sortMundialKnockoutMatches,
} from '../../services/matchdayService';
import {
  filterParticipatingUsers,
} from '../../services/userParticipationService';
import { hydrateMundialMatchesAsync } from '../../services/mundial/mundialKnockoutHydrationService';
import { ResponsiveTableContainer } from '../ui/ResponsiveTableContainer';

const LEAGUE_NAMES: Record<LeagueId, string> = {
  liga_mx: 'Liga BBVA MX',
  mundial_fifa: 'Mundial FIFA 2026',
};

interface FirestoreUser extends User {
  uuid?: string;
}

interface AdminPredictionRow {
  key: string;
  userId: string;
  userName: string;
  match: Match;
  predictionId?: string;
  predicted_score_a: number;
  predicted_score_b: number;
  isMissing: boolean;
}

function getUserDisplayName(user: FirestoreUser): string {
  return user.name || user.displayName || user.email || 'Usuario desconocido';
}

function getPredictionUserId(user: FirestoreUser): string {
  return user.uuid || user.uid || user.id;
}

function getUserCandidateIds(user: FirestoreUser): string[] {
  return [user.uuid, user.uid, user.id].filter(Boolean) as string[];
}

function findPredictionForUserMatch(
  user: FirestoreUser,
  matchId: string,
  predictions: Prediction[]
): Prediction | undefined {
  const candidateIds = new Set(getUserCandidateIds(user));
  return predictions.find(
    (p) => p.match_id === matchId && candidateIds.has(p.user_id)
  );
}

async function fetchPredictionsByMatchIds(matchIds: string[]): Promise<Prediction[]> {
  if (matchIds.length === 0) return [];

  const predictionsRef = collection(db, 'predictions');
  const results: Prediction[] = [];

  for (let i = 0; i < matchIds.length; i += 10) {
    const chunk = matchIds.slice(i, i + 10);
    const snap = await getDocs(query(predictionsRef, where('match_id', 'in', chunk)));
    snap.docs.forEach((docSnap) => {
      results.push({ id: docSnap.id, ...docSnap.data() } as Prediction);
    });
  }

  return results;
}

const ManagePredictions: React.FC = () => {
  const [league, setLeague] = useState<LeagueId>('liga_mx');
  const [rows, setRows] = useState<AdminPredictionRow[]>([]);
  const [missingCount, setMissingCount] = useState(0);
  const [, setLoading] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState('');
  const [selectedJornada, setSelectedJornada] = useState('');
  const [selectedKnockoutPhase, setSelectedKnockoutPhase] = useState('');
  const [phases, setPhases] = useState<string[]>([]);
  const [allMatches, setAllMatches] = useState<Match[]>([]);
  const [showOnlyMissing, setShowOnlyMissing] = useState(false);
  const toast = useToast();
  const { user } = useAuth();

  const hasSelection =
    league === 'mundial_fifa'
      ? Boolean(selectedJornada || selectedKnockoutPhase)
      : Boolean(selectedPhase);
  const selectionLabel =
    league === 'mundial_fifa'
      ? selectedKnockoutPhase ||
        (selectedJornada
          ? getJornadaLabel('mundial_fifa', Number(selectedJornada))
          : '')
      : selectedPhase;

  const knockoutPhases =
    league === 'mundial_fifa'
      ? sortKnockoutPhases(
          Array.from(new Set(getMundialKnockoutMatches(allMatches).map((m) => getKnockoutPhaseLabel(m))))
        )
      : [];

  const visibleRows = showOnlyMissing ? rows.filter((r) => r.isMissing) : rows;

  useEffect(() => {
    loadPhases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league]);

  useEffect(() => {
    if (hasSelection) {
      loadPredictions();
    } else {
      setRows([]);
      setMissingCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhase, selectedJornada, selectedKnockoutPhase, league]);

  const loadPhases = async () => {
    try {
      const matchesRef = collection(db, 'matches');
      const matchesSnap = await getDocs(matchesRef);
      const raw = matchesSnap.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() } as Match))
        .filter((m) => (m.league ?? 'liga_mx') === league);

      const filtered =
        league === 'mundial_fifa' ? await hydrateMundialMatchesAsync(raw) : raw;

      setAllMatches(filtered);

      if (league === 'mundial_fifa') {
        setPhases([]);
        setSelectedPhase('');
        setSelectedJornada('');
        setSelectedKnockoutPhase('');
        return;
      }

      const uniquePhases = Array.from(
        new Set(
          filtered
            .map((m) => m.phase)
            .filter((phase) => phase && parseLigaMxJornadaNumber(phase) !== null)
        )
      ).sort(
        (a, b) =>
          (parseLigaMxJornadaNumber(a) ?? 0) - (parseLigaMxJornadaNumber(b) ?? 0)
      );

      setPhases(uniquePhases);
      setSelectedPhase(uniquePhases[0] ?? '');
      setSelectedJornada('');
    } catch (error) {
      console.error('Error al cargar jornadas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las jornadas',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const resolveMatchIds = (): string[] => {
    if (league === 'mundial_fifa') {
      if (selectedKnockoutPhase) {
        return getMatchIdsForMundialKnockoutPhase(allMatches, selectedKnockoutPhase);
      }
      const jornada = Number(selectedJornada);
      if (!jornada) return [];
      return getMatchIdsForMundialJornada(allMatches, jornada);
    }
    return allMatches.filter((m) => m.phase === selectedPhase).map((m) => m.id);
  };

  const loadPredictions = async () => {
    setLoading(true);
    try {
      const matchIds = resolveMatchIds();
      const filteredMatches = allMatches.filter((m) => matchIds.includes(m.id));
      const matchesData =
        league === 'mundial_fifa' && selectedKnockoutPhase
          ? sortMundialKnockoutMatches(filteredMatches)
          : [...filteredMatches].sort((a, b) =>
              `${a.team_a} vs ${a.team_b}`.localeCompare(`${b.team_a} vs ${b.team_b}`)
            );

      if (matchIds.length === 0) {
        setRows([]);
        setMissingCount(0);
        return;
      }

      const predictionsData = (await fetchPredictionsByMatchIds(matchIds)).filter(
        (p) => p.league === league || (league === 'liga_mx' && !p.league)
      );

      const usersSnap = await getDocs(collection(db, 'users'));
      const usersData = usersSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as FirestoreUser[];

      const participatingUsers = filterParticipatingUsers(usersData, league).sort((a, b) =>
        getUserDisplayName(a).localeCompare(getUserDisplayName(b))
      );

      const matrix: AdminPredictionRow[] = [];

      for (const participant of participatingUsers) {
        const userId = getPredictionUserId(participant);
        if (!userId) continue;

        for (const match of matchesData) {
          const existing = findPredictionForUserMatch(participant, match.id, predictionsData);
          matrix.push({
            key: `${userId}::${match.id}`,
            userId,
            userName: getUserDisplayName(participant),
            match,
            predictionId: existing?.id,
            predicted_score_a: existing?.predicted_score_a ?? 0,
            predicted_score_b: existing?.predicted_score_b ?? 0,
            isMissing: !existing,
          });
        }
      }

      matrix.sort((a, b) => {
        if (a.isMissing !== b.isMissing) return a.isMissing ? -1 : 1;
        if (a.userName !== b.userName) return a.userName.localeCompare(b.userName);
        return `${a.match.team_a} vs ${a.match.team_b}`.localeCompare(
          `${b.match.team_a} vs ${b.match.team_b}`
        );
      });

      setRows(matrix);
      setMissingCount(matrix.filter((r) => r.isMissing).length);
    } catch (error) {
      console.error('Error al cargar predicciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las predicciones',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateRowScores = (
    key: string,
    field: 'predicted_score_a' | 'predicted_score_b',
    value: number
  ) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row))
    );
  };

  const handleSaveRow = async (row: AdminPredictionRow) => {
    const scoreA = row.predicted_score_a;
    const scoreB = row.predicted_score_b;

    if (!Number.isFinite(scoreA) || !Number.isFinite(scoreB) || scoreA < 0 || scoreB < 0) {
      toast({
        title: 'Marcador inválido',
        description: 'Ingresa goles válidos (0 o más).',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      const now = Timestamp.now();

      if (row.predictionId) {
        await updateDoc(doc(db, 'predictions', row.predictionId), {
          predicted_score_a: scoreA,
          predicted_score_b: scoreB,
          updated_at: now,
        });
      } else {
        const newRef = doc(collection(db, 'predictions'));
        await setDoc(newRef, {
          user_id: row.userId,
          match_id: row.match.id,
          league,
          predicted_score_a: scoreA,
          predicted_score_b: scoreB,
          activo: true,
          created_at: now,
          updated_at: now,
        });
      }

      toast({
        title: row.isMissing ? 'Pronóstico asignado' : 'Pronóstico actualizado',
        description: `${row.userName}: ${row.match.team_a} ${scoreA}-${scoreB} ${row.match.team_b}`,
        status: 'success',
        duration: 3000,
      });

      loadPredictions();
    } catch (error) {
      console.error('Error al guardar predicción:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar la predicción',
        status: 'error',
        duration: 3000,
      });
    }
  };

  if (!user || user.email !== 'htorres@teiker.mx') {
    return (
      <Box p={4}>
        <Heading size="md">No tienes acceso a esta página</Heading>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch" p={4}>
      <Heading>Administración de Pronósticos</Heading>

      <FormControl maxW="xs">
        <FormLabel>Liga / Torneo</FormLabel>
        <Select value={league} onChange={(e) => setLeague(e.target.value as LeagueId)}>
          <option value="liga_mx">{LEAGUE_NAMES.liga_mx}</option>
          <option value="mundial_fifa">{LEAGUE_NAMES.mundial_fifa}</option>
        </Select>
      </FormControl>

      <Card>
        <CardHeader>
          <Heading size="md">
            Seleccionar {league === 'mundial_fifa' ? 'jornada o ronda' : 'jornada'} —{' '}
            {LEAGUE_NAMES[league]}
          </Heading>
        </CardHeader>
        <CardBody>
          <FormControl>
            <FormLabel>
              {league === 'mundial_fifa' ? 'Fase de grupos o eliminatorias' : 'Jornada'}
            </FormLabel>
            {league === 'mundial_fifa' ? (
              <>
                <Select
                  placeholder="Jornada de grupos"
                  value={selectedJornada}
                  onChange={(e) => {
                    setSelectedJornada(e.target.value);
                    setSelectedKnockoutPhase('');
                  }}
                  mb={2}
                >
                  {MUNDIAL_GROUP_JORNADAS.map((j) => (
                    <option key={j} value={String(j)}>
                      {getJornadaLabel('mundial_fifa', j)}
                    </option>
                  ))}
                </Select>
                <Select
                  placeholder="Ronda eliminatoria"
                  value={selectedKnockoutPhase}
                  onChange={(e) => {
                    setSelectedKnockoutPhase(e.target.value);
                    setSelectedJornada('');
                  }}
                >
                  {knockoutPhases.map((phase) => (
                    <option key={phase} value={phase}>
                      {phase}
                    </option>
                  ))}
                </Select>
              </>
            ) : (
              <Select
                value={selectedPhase}
                onChange={(e) => setSelectedPhase(e.target.value)}
                placeholder="Selecciona la jornada"
              >
                {phases.map((phase) => (
                  <option key={phase} value={phase}>
                    {phase}
                  </option>
                ))}
              </Select>
            )}
          </FormControl>
          {league === 'mundial_fifa' && hasSelection && (
            <Text fontSize="sm" color="gray.600" mt={3}>
              {resolveMatchIds().length} partido(s) en esta selección
            </Text>
          )}
        </CardBody>
      </Card>

      {hasSelection && (
        <Card>
          <CardHeader>
            <VStack align="stretch" spacing={2}>
              <Heading size="md">Pronósticos — {selectionLabel}</Heading>
              <HStack spacing={4} flexWrap="wrap">
                <Text fontSize="sm" color="gray.600">
                  {rows.length - missingCount} capturados · {missingCount} sin captura
                </Text>
                <Button
                  size="xs"
                  variant={showOnlyMissing ? 'solid' : 'outline'}
                  colorScheme="orange"
                  onClick={() => setShowOnlyMissing((v) => !v)}
                >
                  {showOnlyMissing ? 'Ver todos' : 'Solo sin captura'}
                </Button>
              </HStack>
            </VStack>
          </CardHeader>
          <CardBody>
            {visibleRows.length === 0 ? (
              <Text color="gray.500">
                {showOnlyMissing
                  ? 'Todos los usuarios activos capturaron esta jornada.'
                  : 'No hay usuarios activos o partidos en esta jornada.'}
              </Text>
            ) : (
              <ResponsiveTableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Estado</Th>
                    <Th>Usuario</Th>
                    <Th>Partido</Th>
                    <Th isNumeric>Local</Th>
                    <Th isNumeric>Visitante</Th>
                    <Th>Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {visibleRows.map((row) => (
                    <Tr
                      key={row.key}
                      bg={row.isMissing ? 'orange.50' : undefined}
                      _dark={{ bg: row.isMissing ? 'whiteAlpha.100' : undefined }}
                    >
                      <Td>
                        {row.isMissing ? (
                          <Badge colorScheme="orange">Sin captura</Badge>
                        ) : (
                          <Badge colorScheme="green">Capturado</Badge>
                        )}
                      </Td>
                      <Td>{row.userName}</Td>
                      <Td>
                        {row.match.team_a} vs {row.match.team_b}
                        {row.match.bracket_slot && (
                          <Badge ml={2} colorScheme="purple" fontSize="xs">
                            {row.match.bracket_slot}
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <NumberInput
                          min={0}
                          max={20}
                          size="sm"
                          value={row.predicted_score_a}
                          onChange={(_, value) =>
                            updateRowScores(row.key, 'predicted_score_a', value)
                          }
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </Td>
                      <Td>
                        <NumberInput
                          min={0}
                          max={20}
                          size="sm"
                          value={row.predicted_score_b}
                          onChange={(_, value) =>
                            updateRowScores(row.key, 'predicted_score_b', value)
                          }
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </Td>
                      <Td>
                        <Button
                          colorScheme={row.isMissing ? 'orange' : 'blue'}
                          size="sm"
                          onClick={() => handleSaveRow(row)}
                        >
                          {row.isMissing ? 'Asignar' : 'Guardar'}
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              </ResponsiveTableContainer>
            )}
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default ManagePredictions;
